import React, { useEffect, useMemo, useState } from "react";
import { Plus, X } from "lucide-react";

const API = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000";
const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const inputCls = "w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";
const labelCls = "text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5";

export default function AssignmentRules() {
  const [categories, setCategories] = useState([]);
  const [queues,     setQueues]     = useState([]);
  const [rules,      setRules]      = useState([]);
  const [draft,      setDraft]      = useState({ category: "", team: "", default_priority: "P3" });
  const [loading,    setLoading]    = useState(true);
  const [saving,     setSaving]     = useState(false);
  const [msg,        setMsg]        = useState(null);
  const [showForm,   setShowForm]   = useState(false);
  const token = localStorage.getItem("token");

  const load = async () => {
    setLoading(true);
    try {
      const [c, q, r] = await Promise.all([
        fetch(`${API}/api/admin/tickets/categories`, { headers: { Authorization: `Bearer ${token}` } }).then(x => x.json()),
        fetch(`${API}/api/admin/tickets/queues`,     { headers: { Authorization: `Bearer ${token}` } }).then(x => x.json()),
        fetch(`${API}/api/admin/tickets/rules`,      { headers: { Authorization: `Bearer ${token}` } }).then(x => x.json()),
      ]);
      setCategories(Array.isArray(c) ? c : []);
      setQueues(Array.isArray(q) ? q : []);
      setRules(Array.isArray(r) ? r : []);
    } catch { setCategories([]); setQueues([]); setRules([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const catNames   = useMemo(() => categories.map(c => c.key || c),   [categories]);
  const queueNames = useMemo(() => queues.map(q => q.name || q),      [queues]);

  const save = async () => {
    if (!draft.category || !draft.team) return;
    setSaving(true); setMsg(null);
    try {
      await fetch(`${API}/api/admin/tickets/rules`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(draft),
      });
      setDraft({ category: "", team: "", default_priority: "P3" }); setShowForm(false);
      setMsg("✓ Rule saved."); await load();
    } catch (e) { setMsg(`⚠ ${e.message}`); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    await fetch(`${API}/api/admin/tickets/rules/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
    setRules(r => r.filter(x => x.id !== id));
  };

  return (
    <div className={card}>
      <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
        <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Assignment Rules</span>
        <div className="flex items-center gap-2">
          <span className="text-xs text-white/30">{rules.length} rules</span>
          <button onClick={() => setShowForm(v => !v)}
            className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-[#00ff90]/25 bg-[#00ff90]/8 text-[#00ff90]/70 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors">
            <Plus size={12} /> Add
          </button>
        </div>
      </div>
      {showForm && (
        <div className="px-5 py-4 border-b border-white/5 bg-[#00ff90]/3 space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div><label className={labelCls}>Category</label>
              <select className={inputCls} value={draft.category} onChange={e => setDraft(d => ({...d, category: e.target.value}))}>
                <option value="">Select…</option>
                {catNames.map(n => <option key={n}>{n}</option>)}
              </select></div>
            <div><label className={labelCls}>Assign to Team</label>
              <select className={inputCls} value={draft.team} onChange={e => setDraft(d => ({...d, team: e.target.value}))}>
                <option value="">Select…</option>
                {queueNames.map(n => <option key={n}>{n}</option>)}
              </select></div>
            <div><label className={labelCls}>Default Priority</label>
              <select className={inputCls} value={draft.default_priority} onChange={e => setDraft(d => ({...d, default_priority: e.target.value}))}>
                {["P1","P2","P3","P4"].map(p => <option key={p}>{p}</option>)}
              </select></div>
          </div>
          {msg && <p className={`text-xs ${msg.startsWith("⚠") ? "text-red-400" : "text-[#00ff90]"}`}>{msg}</p>}
          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowForm(false)} className="text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white transition-colors">Cancel</button>
            <button onClick={save} disabled={saving || !draft.category || !draft.team}
              className="text-xs px-4 py-1.5 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors">
              {saving ? "Saving…" : "Save Rule"}
            </button>
          </div>
        </div>
      )}
      <div className="divide-y divide-white/5 max-h-64 overflow-y-auto">
        {loading ? <div className="p-4"><div className="h-8 rounded-lg bg-white/5 animate-pulse" /></div>
        : rules.length === 0 ? <p className="px-5 py-4 text-white/20 text-xs italic text-center">No rules configured.</p>
        : rules.map(r => (
          <div key={r.id} className="flex items-center justify-between px-5 py-3">
            <p className="text-sm text-white/60">
              <span className="text-white/70 font-medium">{r.category}</span>
              <span className="text-white/30"> → </span>
              <span className="text-white/70">{r.team}</span>
              <span className="text-white/30"> · default </span>
              <span className="text-white/50">{r.default_priority}</span>
            </p>
            <button onClick={() => remove(r.id)} className="w-7 h-7 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-white/30 hover:text-red-400 transition-colors"><X size={12} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}